import type { PointerEvent, ReactNode } from 'react';
import { cn } from '@core/ui/cn';
import type { SplitRow } from './SplitGrid';

// Действия строки (добавить, править, скрыть) в одном слоте у правого края подписи. Слот один на
// все строки, поэтому кнопки не образуют рваный столбец; последней ставится «+», чтобы плюсы
// проектов, подпроектов и дорожек стояли на одной вертикали. Родитель — `relative`. Показ либо
// по CSS (родитель ещё и `group`: строка под курсором), либо пропом show (Projects: весь блок
// проекта, пока курсор над любой его строкой).
export function RowActions({ bg, show, children }: { bg: string; show?: boolean; children: ReactNode }) {
  return (
    <span className={cn('absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5 pl-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100', show && 'opacity-100', bg)}>
      {children}
    </span>
  );
}

export const rowActionButton = 'min-h-0 w-7 h-7 rounded hover:bg-gray-200 shrink-0 leading-none';

/** Ручка перестановки строки за «⋮⋮»: место занимает всегда, видна как остальные действия строки — под курсором (родитель `group`) или по show. */
export function ReorderHandle({ label, show, onPointerDown }: { label: string; show?: boolean; onPointerDown: (e: PointerEvent<HTMLElement>) => void }) {
  return (
    <span role="button" aria-label={`Reorder ${label}`} title="Drag to reorder" className={cn('min-h-0 cursor-grab text-ink-muted select-none px-1 opacity-0 group-hover:opacity-100 focus-visible:opacity-100', show && 'opacity-100')} onPointerDown={onPointerDown}>⋮⋮</span>
  );
}
export const GHOST_ROW = 32;
const ghostBox = 'absolute inset-x-1 inset-y-1 rounded-md border-2 border-dashed border-accent bg-accent/15';
/**
 * Строка-призрак на месте приземления при перестановке строк: соседи раздвигаются, как под контур
 * задачи. Тело несёт data-row-ghost — useRowReorder под ним держит прежнюю цель, а не теряет её.
 */
export function reorderGhostRow(label: string, color?: string): SplitRow {
  return {
    key: '__reorder-ghost', height: GHOST_ROW, className: 'border-b border-gray-200', labelClassName: 'bg-gray-50', gridClassName: 'bg-white',
    label: (
      <div className="relative h-full">
        <div className={cn(ghostBox, 'flex items-center gap-2 px-2 text-sm font-semibold text-accent')}>
          {color && <span className="w-3 h-3 rounded-full shrink-0" style={{ background: color }} aria-hidden />}
          <span className="truncate">{label}</span>
        </div>
      </div>
    ),
    gridAttrs: { 'data-row-ghost': '' },
    grid: <div className={ghostBox} />,
  };
}

/** Строки сетки с призраком перед строкой at (null — без призрака). */
export const withGhostRow = (rows: SplitRow[], at: number | null, ghost: SplitRow): SplitRow[] => (at === null ? rows : [...rows.slice(0, at), ghost, ...rows.slice(at)]);

// «+» как у кнопки + Task в тулбаре: белый на синем, на всю кнопку
export const rowAddButton = 'min-h-0 w-7 h-7 rounded bg-accent text-white hover:bg-accent-strong shrink-0 text-lg font-bold leading-none';
